import type { Message } from '@centraoneplus/shared-types';
import { needsEnglish } from '@/features/voice/utils/languageName';
import { readVoiceTurn } from './voiceTranscript';

/**
 * Translating a conversation on request.
 *
 * Calls and chats store an English rendering with each turn as it happens
 * (see voiceTranscript.ts). An email does not, and neither does anything
 * written before that existed - so those are translated when an agent asks,
 * and only then: a translation costs a call to the translator per message.
 *
 * Kept pure so what counts as "needs translating" can be tested on its own.
 */

/** A translation made on request, held for the session. */
export interface MessageTranslation {
  text: string;
  /** The language the message was written in, when the translator said. */
  language?: string;
  /** True when the message was too long and only its beginning was translated. */
  truncated?: boolean;
}

/**
 * What a message says, as plain text. An email's body is HTML; the translator
 * wants the words, not the markup, and never a style block or a script.
 */
export function messagePlainText(message: Pick<Message, 'content'> | undefined | null): string {
  const content = message?.content ?? '';
  if (!/<[a-z!][\s\S]*>/i.test(content)) return content.trim();
  const withBreaks = content
    .replace(/<(style|script)[\s\S]*?<\/\1>/gi, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/\s*(p|div|li|tr|h[1-6]|blockquote)\s*>/gi, '\n');
  const text = new DOMParser().parseFromString(withBreaks, 'text/html').body.textContent ?? '';
  return text
    .replace(/[ \t ]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * True for a message an agent may not be able to read: written in a script
 * other than Latin, with no English rendering stored. System notices are
 * ours and already English.
 *
 * Judged by script, not by a language tag: a tag is absent on email, and a
 * Hindi speaker's "OK, thanks" needs no translation.
 */
export function needsTranslation(message: Message | undefined | null): boolean {
  if (!message || message.senderType === 'system') return false;
  if (readVoiceTurn(message)?.english) return false;
  return needsEnglish(messagePlainText(message));
}

/** The messages of a thread still to translate, given those already done. */
export function untranslated(
  messages: Message[] | undefined | null,
  done: Record<string, MessageTranslation>,
): Message[] {
  if (!Array.isArray(messages)) return [];
  return messages.filter((m) => !done[m.id] && needsTranslation(m));
}
